import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useAuth } from '../../context/AuthContext';
import { useSidebar } from '../../context/SidebarContext';
import { useTheme } from '../../context/ThemeContext';

interface NavItem {
  path: string;
  label: string;
  icon: string;
  module?: string; // Permission module key
}

const mainNav: NavItem[] = [
  { path: '/', label: 'Dashboard', icon: 'solar:home-2-bold-duotone', module: 'dashboard' },
  { path: '/clients', label: 'Clients', icon: 'solar:user-bold-duotone', module: 'clients' },
  { path: '/inventory', label: 'Map Inventory', icon: 'solar:map-bold-duotone', module: 'inventory' },
  { path: '/sales', label: 'Sales Pipeline', icon: 'solar:sale-bold-duotone', module: 'sales' },
  { path: '/payments', label: 'Payments', icon: 'solar:wallet-money-bold-duotone', module: 'payments' },
  { path: '/rules', label: 'Commission Rules', icon: 'solar:hand-money-bold-duotone', module: 'commission_rules' },
  { path: '/users', label: 'Users & Roles', icon: 'solar:users-group-rounded-bold-duotone', module: 'users' },
  { path: '/reports', label: 'Reports', icon: 'solar:chart-2-bold-duotone', module: 'reports' },
];

const bottomNav: NavItem[] = [
  { path: '/refund-policies', label: 'Refund Policies', icon: 'solar:wallet-money-bold-duotone', module: 'refund_policies' },
  { path: '/settings', label: 'Settings', icon: 'solar:settings-bold-duotone', module: 'settings' },
];

const EXPANDED_WIDTH = 240;
const COLLAPSED_WIDTH = 64;

const Sidebar: React.FC = () => {
  const location = useLocation();
  const { logout, hasModuleAccess, isSuperAdmin, hasAnyPermissions } = useAuth();
  const { collapsed, toggle } = useSidebar();
  const { theme, toggleTheme } = useTheme();

  const width = collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const navItemStyle = (active: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: collapsed ? '0px' : '12px',
    padding: collapsed ? '10px 0' : '10px 12px',
    justifyContent: collapsed ? 'center' : 'flex-start',
    borderRadius: 'var(--radius-md)',
    color: active ? 'var(--nav-item-active-color)' : 'var(--text-muted)',
    fontSize: '14px',
    fontWeight: active ? 600 : 500,
    textDecoration: 'none',
    transition: 'all 0.2s ease',
    marginBottom: '4px',
    background: active ? 'var(--nav-item-active-bg)' : 'transparent',
    fontFamily: 'var(--font-body)',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  });

  return (
    <aside style={{
      width: `${width}px`,
      minWidth: `${width}px`,
      height: '100vh',
      position: 'fixed',
      left: 0,
      top: 0,
      background: 'var(--nav-bg)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      borderRight: '1px solid var(--nav-border)',
      padding: collapsed ? '24px 10px' : '24px 16px',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 100,
      transition: 'all 0.2s ease',
      overflow: 'hidden',
    }}>
      {/* Logo + Toggle */}
      <div style={{
        position: 'relative',
        marginBottom: '32px',
        paddingBottom: '16px',
        borderBottom: collapsed ? 'none' : '1px solid var(--nav-border)',
      }}>
        {collapsed ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src="/voplogo.png" alt="VOP CRM" width={32} style={{ flexShrink: 0 }} />
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <img src="/voplogo.png" alt="VOP" width={40} height={40} style={{ borderRadius: '10px', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: '15px', color: 'var(--text-primary)' }}>Admin Portal</div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Valley of Peace</div>
            </div>
            <button onClick={toggle} title="Collapse sidebar" style={{
              background: 'rgba(148,163,184,0.08)', border: 'none', borderRadius: '6px',
              width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: 'var(--dark-500)', transition: 'all 0.15s',
              flexShrink: 0,
            }}>
              <Icon icon="solar:sidebar-minimalistic-bold-duotone" width={16} />
            </button>
          </div>
        )}
      </div>

      {/* Expand button when collapsed */}
      {collapsed && (
        <button onClick={toggle} title="Expand sidebar" style={{
          background: 'rgba(148,163,184,0.08)', border: 'none', borderRadius: '6px',
          width: '36px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', color: 'var(--dark-500)', transition: 'all 0.15s',
          margin: '0 auto 16px',
        }}>
          <Icon icon="solar:hamburger-menu-bold-duotone" width={16} />
        </button>
      )}

      {/* Main Nav */}
      <nav style={{ flex: 1 }}>
        {mainNav.filter(item => !hasAnyPermissions || !item.module || isSuperAdmin || hasModuleAccess(item.module)).map((item) => (
          <NavLink key={item.path} to={item.path} style={navItemStyle(isActive(item.path))}
            title={collapsed ? item.label : undefined}>
            <Icon icon={item.icon} width={20} style={{ flexShrink: 0 }} />
            {!collapsed && <span>{item.label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* Divider */}
      <div style={{
        height: '1px',
        background: 'linear-gradient(90deg, transparent, rgba(148, 163, 184, 0.15), transparent)',
        margin: '12px 0',
      }} />

      {/* Bottom Nav */}
      {bottomNav.filter(item => !hasAnyPermissions || !item.module || isSuperAdmin || hasModuleAccess(item.module)).map((item) => (
        <NavLink key={item.path} to={item.path} style={navItemStyle(isActive(item.path))}
          title={collapsed ? item.label : undefined}>
          <Icon icon={item.icon} width={20} style={{ flexShrink: 0 }} />
          {!collapsed && <span>{item.label}</span>}
        </NavLink>
      ))}

      {/* Theme Toggle */}
      <button
        onClick={toggleTheme}
        title={theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: collapsed ? '0px' : '12px',
          padding: collapsed ? '10px 0' : '10px 12px',
          justifyContent: collapsed ? 'center' : 'flex-start',
          borderRadius: 'var(--radius-md)',
          color: 'var(--text-muted)',
          fontSize: '14px',
          fontWeight: 500,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontFamily: 'var(--font-body)',
          transition: 'all 0.2s ease',
          width: '100%',
          textAlign: 'left',
          marginTop: '4px',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        }}
      >
        <Icon
          icon={theme === 'light' ? 'solar:moon-bold-duotone' : 'solar:sun-bold-duotone'}
          width={20}
          style={{ flexShrink: 0 }}
        />
        {!collapsed && <span>{theme === 'light' ? 'Dark Mode' : 'Light Mode'}</span>}
      </button>

      {/* Logout */}
      <button
        onClick={logout}
        title={collapsed ? 'Logout' : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: collapsed ? '0px' : '12px',
          padding: collapsed ? '10px 0' : '10px 12px',
          justifyContent: collapsed ? 'center' : 'flex-start',
          borderRadius: 'var(--radius-md)',
          color: 'var(--text-muted)',
          fontSize: '14px',
          fontWeight: 500,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontFamily: 'var(--font-body)',
          transition: 'all 0.2s ease',
          width: '100%',
          textAlign: 'left',
          marginTop: '4px',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        }}
      >
        <Icon icon="solar:logout-2-bold-duotone" width={20} style={{ flexShrink: 0 }} />
        {!collapsed && <span>Logout</span>}
      </button>
    </aside>
  );
};

export { EXPANDED_WIDTH, COLLAPSED_WIDTH };
export default Sidebar;
